import * as ShopifyCart from '@shopify/theme-cart';

import { getJSONfromDOM, globalEventBus } from '@pixelcabin/ui-utils';

import {
  matchCostPerItemTag,
  getTotalItems
} from '../PurchaseForm/cost_per_item';
import camelize from '../../helper/camelize';

export const flyoutCart = 'flyoutCart';

export const mutations = {
  FETCH_STATE: 'FETCH_STATE',
  FETCH_STATE_SUCCESS: 'FETCH_STATE_SUCCESS',
  FETCH_STATE_ERROR: 'FETCH_STATE_ERROR',
  UPDATE: 'UPDATE',
  CLEAR_FLASH_MESSAGE: 'CLEAR_FLASH_MESSAGE'
};

export const actions = {
  fetchState: 'fetchState',
  updateItem: 'updateItem',
  removeItem: 'removeItem'
};

export const FREE_GIFT_PROPERTY = 'free_gift_with_machine';

const addedToCartLessThanRequested = (newItem, updateRequest) => {
  return (
    updateRequest &&
    updateRequest.item.id === newItem.id &&
    updateRequest.quantity > newItem.quantity
  );
};

const isFreeGift = (item, freeGiftVariantId) => {
  if (
    item.properties &&
    Object.prototype.hasOwnProperty.call(item.properties, FREE_GIFT_PROPERTY)
  ) {
    return true;
  } else if (item.id === freeGiftVariantId) {
    return true;
  }
};

/**
 * If there is a discounted machine, but not enough subscription items
 * we remove the discounted machine and add its standard-priced variant.
 */
const handleDiscountedMachine = ({ getters, dispatch }) => {
  if (getters.discountedMachines.length && !getters.hasMinSubscriptionOrder) {
    return Promise.all(
      getters.discountedMachines.map(discountedMachine => {
        const { key, undiscountedVariantId, quantity } = discountedMachine;

        return ShopifyCart.removeItem(key).then(() => {
          return ShopifyCart.addItem(undiscountedVariantId, { quantity });
        });
      })
    )
      .then(() => {
        dispatch(actions.fetchState);
      })
      .catch(console.error);
  }
};

const handleFreeGift = ({ getters, state, dispatch }) => {
  if (getters.hasMachines && !getters.freeGift) {
    // Add free gift
    return ShopifyCart.addItem(state.freeGiftVariantId, {
      quantity: 1,
      properties: {
        [FREE_GIFT_PROPERTY]: true
      }
    })
      .then(() => {
        dispatch(actions.fetchState);
      })
      .catch(console.error);
  } else if (!getters.hasMachines && getters.freeGift) {
    // Remove free gift
    dispatch(actions.removeItem, { item: getters.freeGift });
  }
};

export const flyoutCartStore = () => {
  const initialData = getJSONfromDOM(['#pxl-flyout-cart-data']);

  initialData.freeGiftVariantId = parseInt(initialData.freeGiftVariantId);

  return {
    namespaced: true,
    state: Object.assign(
      {
        isLoading: false,
        items: [],
        token: '',
        subTotal: 0,
        itemCount: 0
      },
      initialData
    ),
    getters: {
      machines: state => {
        return state.items.filter(item => item.type === 'Machines');
      },

      discountedMachines: (state, getters) => {
        return getters.machines.filter(
          machine => machine.options.purchaseType === 'subscription'
        );
      },

      hasMachines: (state, getters) => {
        return getters.machines.length > 0;
      },

      subscriptionItems: state => {
        if (__SHOPIFY_SUBSCRIPTIONS__) {
          return state.items.filter(item => item.sellingPlanAllocation);
        } else {
          return state.items.filter(
            item =>
              item.properties && item.properties.shipping_interval_frequency
          );
        }
      },

      capsuleSubscriptionItems: (state, getters) => {
        return getters.subscriptionItems.filter(item =>
          item.tags.some(tag => tag.startsWith('capsules-per-box:'))
        );
      },

      hasSubscriptionItems: (state, getters) => {
        return getters.subscriptionItems.length > 0;
      },

      hasMinSubscriptionOrder: (state, getters) => {
        if (getters.capsuleSubscriptionItems.length > 0) {
          const totalCapsules = getTotalItems(getters.capsuleSubscriptionItems);

          return totalCapsules >= state.minSubscriptionOrderSize;
        }

        return false;
      },

      isEmpty: state => {
        return state.items.length === 0;
      },

      freeGift: state => {
        return state.items.find(item =>
          isFreeGift(item, state.freeGiftVariantId)
        );
      }
    },
    mutations: {
      [mutations.FETCH_STATE](state) {
        state.isLoading = true;
      },

      [mutations.FETCH_STATE_SUCCESS](state) {
        state.isLoading = false;
      },

      [mutations.FETCH_STATE_ERROR](state) {
        state.isLoading = false;
      },

      [mutations.UPDATE](state, toUpdate) {
        Object.entries(toUpdate).forEach(
          ([key, value]) => (state[key] = value)
        );
      },

      [mutations.CLEAR_FLASH_MESSAGE](state, { id }) {
        state.items = state.items.map(item => {
          if (item.id === id) {
            item.flashMessage = false;
          }

          return item;
        });
      }
    },
    actions: {
      [actions.fetchState](
        { commit, dispatch, state, getters },
        updateRequest
      ) {
        commit(mutations.FETCH_STATE);

        Promise.all([
          ShopifyCart.getState(),
          // custom AJAX endpoint that supplies metafield data to cart items
          fetch('/cart?view=metafields.json').then(res => {
            return res.json().catch(console.error);
          })
        ])
          .then(([shopifyCartState, metafields = { items: {} }]) => {
            const { items, token, total_price, item_count } = shopifyCartState;

            const updatedItems = items.map(item => {
              const {
                featured_image,
                final_line_price,
                handle,
                id,
                key,
                product_title,
                product_type,
                properties,
                quantity,
                selling_plan_allocation,
                url
              } = item;

              const toMerge = Object.prototype.hasOwnProperty.call(
                metafields.items,
                id
              )
                ? metafields.items[id]
                : {};

              const subtitle = item.options_with_values.reduce(
                (subtitle, option) => {
                  if (
                    !['title', 'purchase type'].includes(
                      option.name.toLowerCase()
                    )
                  ) {
                    subtitle = option.value;
                  }

                  return subtitle;
                },
                false
              );

              if (subtitle) {
                toMerge.subtitle = subtitle;
              }

              if (addedToCartLessThanRequested(item, updateRequest)) {
                toMerge.flashMessage = state.notEnoughStockText;
              }

              const options = item.options_with_values.reduce(
                (result, { name, value }) => {
                  result[camelize(name)] = camelize(value);

                  return result;
                },
                {}
              );

              const component = isFreeGift(item, state.freeGiftVariantId)
                ? 'FreeGiftLineItem'
                : 'LineItem';

              const multiplier = (tags => {
                if (!tags || tags.length === 0) {
                  return 1;
                }

                let result = 1;

                matchCostPerItemTag(tags, ([, matchedValue]) => {
                  result = matchedValue;
                });

                return result;
              })(toMerge.tags);

              const compareAtPrice = (compareAtPrice => {
                if (!compareAtPrice) return '';

                return compareAtPrice * quantity;
              })(toMerge.compareAtPrice, quantity);

              return {
                handle,
                image: {
                  alt: featured_image.alt,
                  src: featured_image.url
                },
                id,
                key,
                price: final_line_price,
                properties,
                quantity,
                title: toMerge.original_title || product_title,
                type: product_type,
                url,
                options,
                component,
                sellingPlanAllocation: selling_plan_allocation,
                multiplier,
                ...toMerge,
                compareAtPrice
              };
            });

            commit(mutations.UPDATE, {
              token,
              subTotal: total_price,
              itemCount: item_count,
              items: updatedItems
            });

            commit(mutations.FETCH_STATE_SUCCESS);

            globalEventBus.emit('flyoutCart.updatedState', {
              ...state,
              ...getters
            });

            // New Shopify Subscriptions API currently
            // incompatible with line item Shopify Scripts
            if (!__SHOPIFY_SUBSCRIPTIONS__) {
              handleFreeGift({ getters, state, dispatch });
            }

            handleDiscountedMachine({ getters, dispatch });
          })
          .catch(err => {
            console.error(err);
            commit(mutations.FETCH_STATE_ERROR);
          });
      },

      [actions.updateItem]({ commit, dispatch }, { item, quantity }) {
        commit(mutations.FETCH_STATE);

        const { key } = item;

        return ShopifyCart.updateItem(key, { quantity })
          .then(() => {
            const updateRequest = { item, quantity };

            dispatch(actions.fetchState, updateRequest);
          })
          .catch(console.error);
      },

      [actions.removeItem]({ commit, dispatch }, { item }) {
        commit(mutations.FETCH_STATE);

        const { key } = item;

        this.isUpdating = true;

        ShopifyCart.removeItem(key)
          .then(() => {
            globalEventBus.emit('flyoutCart.removeItem', item);

            dispatch(actions.fetchState);
          })
          .catch(console.error);
      }
    }
  };
};
